'use client';

import { useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useToast } from '@/shared/components/ui/use-toast';
import { storyKeys } from '@/contexts/stories/queryKeys';
import { respondToPublicationInvite } from '../actions/publicationInvite';

/**
 * Shown on the owner's story page while an admin nomination is open
 * (stories.public_status = 'nominated'). Accepting records the same consent the
 * creation wizard records (the disclosure copy is the wizard's, one source), so
 * the story enters admin review; declining just closes the invite.
 */
export function PublicationInviteBanner({ storyId }: { storyId: string }) {
  const t = useTranslations('stories.workspace.publicationInvite');
  const tConsent = useTranslations('content.wizard.output');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [pending, startTransition] = useTransition();
  const [details, setDetails] = useState(false);
  const [done, setDone] = useState<'accepted' | 'declined' | null>(null);

  const respond = (accept: boolean) =>
    startTransition(async () => {
      const res = await respondToPublicationInvite(storyId, accept);
      if (!res.success) {
        toast({ variant: 'destructive', description: res.error });
        return;
      }
      setDone(accept ? 'accepted' : 'declined');
      queryClient.invalidateQueries({ queryKey: storyKeys.detail(storyId) });
    });

  if (done) {
    return (
      <div className="rounded-lg border bg-muted/40 p-3 text-sm" data-testid="publication-invite-done">
        {done === 'accepted' ? t('accepted') : t('declined')}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 sm:p-4 space-y-2" data-testid="publication-invite">
      <div className="flex items-start gap-2">
        <Globe className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium">{t('title')}</p>
          <p className="text-xs text-muted-foreground">{t('body')}</p>
          <button
            type="button"
            className="text-xs text-primary underline"
            onClick={() => setDetails((v) => !v)}
            data-testid="publication-invite-details-toggle"
          >
            {tConsent('publicConsentSummary')}
          </button>
          {details && (
            <p className="text-xs text-muted-foreground" data-testid="publication-invite-details">
              {tConsent('publicConsentDetails')}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pl-6">
        <Button size="sm" disabled={pending} onClick={() => respond(true)} data-testid="publication-invite-accept">
          {t('accept')}
        </Button>
        <Button size="sm" variant="ghost" disabled={pending} onClick={() => respond(false)} data-testid="publication-invite-decline">
          {t('decline')}
        </Button>
      </div>
    </div>
  );
}
